// LazyLoadErrorBoundary — 2026-05-18
// ============================================================
// Prinde erori la încărcarea de chunk-uri lazy (code-splitting) și
// auto-reload pagina dacă e o eroare tranzitorie de deploy.
//
// Context: Vercel SPA cu code-splitting deployează main.js + chunks
// în waves. Există un window de 5-15 min în care main.js referează
// chunk-uri ce nu sunt încă deployate → Vercel SPA rewrite returnează
// index.html pentru chunk URLs → browser parsa HTML ca JS → SyntaxError
// "Unexpected token '<'" → React mount fails → pagină blank.
//
// Soluție: Detect chunk load errors, sessionStorage retry counter, auto
// reload după 2.5s. Reset counter la mount reușit. După MAX_RETRIES,
// afișează manual refresh button.
//
// Plasare: în App.js, în jurul Suspense (NU în interior — erorile lazy
// se aruncă sincron, înainte ca Suspense să poată afișa fallback).
// ============================================================

import React from 'react';
import { withTranslation } from 'react-i18next';

const RETRY_KEY = 'pb_lazy_retry_count';
const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 2500;

function isChunkLoadError(error) {
  if (!error) return false;
  const name = String(error.name || '');
  const msg = String(error.message || '');
  return (
    name === 'ChunkLoadError' ||
    /Loading chunk\s+\S+\s+failed/i.test(msg) ||
    /Loading CSS chunk/i.test(msg) ||
    /Unexpected token\s+'<'/i.test(msg) ||  // Vercel returns HTML for missing chunks
    /failed to fetch dynamically imported module/i.test(msg) ||
    /import\(\) failed/i.test(msg)
  );
}

class LazyLoadErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, isChunkError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      isChunkError: isChunkLoadError(error),
      error,
    };
  }

  componentDidCatch(error, info) {
    // Log pentru audit (Sentry va prinde oricum din window.onerror)
    if (typeof console !== 'undefined') {
      console.error('[LazyLoadErrorBoundary]', error?.name, error?.message, info?.componentStack);
    }
    if (isChunkLoadError(error)) {
      let retryCount = 0;
      try {
        retryCount = parseInt(sessionStorage.getItem(RETRY_KEY) || '0', 10) || 0;
      } catch { /* sessionStorage poate fi blocat (Safari privat etc.) — continuăm cu 0 */ }
      if (retryCount < MAX_RETRIES) {
        try { sessionStorage.setItem(RETRY_KEY, String(retryCount + 1)); } catch {}
        setTimeout(() => {
          // 2026-05-18 — IMPORTANT: aplicația are Service Worker (sw.js) cu cache-first
          // pe assets. Dacă SW a cache-uit răspunsul HTML stricat din deploy window,
          // window.location.reload() NU îl invalidează — SW interceptează fetch-urile.
          // Soluție: clear toate Cache API stores + unregister SW + reload cu cache-bust.
          (async () => {
            try {
              // 1. Clear toate Cache API stores (chunks JS/CSS cache-uite stricat)
              if (typeof caches !== 'undefined' && caches.keys) {
                const keys = await caches.keys();
                await Promise.all(keys.map((k) => caches.delete(k)));
              }
            } catch (e) { /* ignore */ }
            try {
              // 2. Unregister Service Worker (la next reload se re-inregistreaza cu version noua)
              if (navigator.serviceWorker && navigator.serviceWorker.getRegistrations) {
                const regs = await navigator.serviceWorker.getRegistrations();
                await Promise.all(regs.map((r) => r.unregister()));
              }
            } catch (e) { /* ignore */ }
            // 3. Reload cu cache-bust query param — forțează browser-ul să nu folosească
            // resurse cached pentru request-ul URL-ului paginii (chunks au max-age=0 oricum,
            // dar SW removed = fetch direct merge la server).
            const url = window.location.pathname + '?_pb_reload=' + Date.now() + window.location.hash;
            window.location.replace(url);
          })();
        }, RETRY_DELAY_MS);
      }
    }
  }

  componentDidUpdate(prevProps, prevState) {
    // Reset counter dacă child-ul s-a montat OK (no error this render).
    // Asta previne ca un singur succes să mențină counter-ul ridicat.
    if (prevState.hasError && !this.state.hasError) {
      try { sessionStorage.removeItem(RETRY_KEY); } catch {}
    }
  }

  render() {
    const { t } = this.props;
    if (this.state.hasError && this.state.isChunkError) {
      let retryCount = 0;
      try {
        retryCount = parseInt(sessionStorage.getItem(RETRY_KEY) || '0', 10) || 0;
      } catch { /* ignore */ }

      // În timpul auto-reload (retryCount ≤ MAX_RETRIES) — mesaj prietenos
      if (retryCount <= MAX_RETRIES) {
        return (
          <div
            role="status"
            aria-live="polite"
            style={{
              minHeight: '70vh',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 16,
              padding: 24,
              fontFamily: 'DM Mono, monospace',
              textAlign: 'center',
            }}
          >
            <div style={{ fontSize: 16, color: '#D4AF37', letterSpacing: 3, textTransform: 'uppercase' }}>
              🔄 {t('app.lazy_error.updating_title')}
            </div>
            <div style={{ fontSize: 13, color: '#888', maxWidth: 420, lineHeight: 1.5, fontFamily: 'Inter, system-ui, sans-serif' }}>
              {t('app.lazy_error.updating_message')}
            </div>
            <div style={{ fontSize: 11, color: '#aaa' }}>
              {t('app.lazy_error.attempt', { current: retryCount, max: MAX_RETRIES })}
            </div>
          </div>
        );
      }

      // Max retries depășit — manual refresh
      return (
        <div
          role="alert"
          style={{
            minHeight: '70vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 16,
            padding: 24,
            fontFamily: 'DM Mono, monospace',
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: 16, color: '#c0392b', letterSpacing: 2, textTransform: 'uppercase' }}>
            ⚠️ {t('app.lazy_error.cannot_load_title')}
          </div>
          <div style={{ fontSize: 13, color: '#888', maxWidth: 420, lineHeight: 1.5, fontFamily: 'Inter, system-ui, sans-serif' }}>
            {t('app.lazy_error.cannot_load_message', { max: MAX_RETRIES })}
          </div>
          <button
            onClick={async () => {
              try { sessionStorage.removeItem(RETRY_KEY); } catch {}
              // Aggressive clear (vezi componentDidCatch pentru detalii)
              try {
                if (typeof caches !== 'undefined' && caches.keys) {
                  const keys = await caches.keys();
                  await Promise.all(keys.map((k) => caches.delete(k)));
                }
              } catch {}
              try {
                if (navigator.serviceWorker && navigator.serviceWorker.getRegistrations) {
                  const regs = await navigator.serviceWorker.getRegistrations();
                  await Promise.all(regs.map((r) => r.unregister()));
                }
              } catch {}
              window.location.replace(window.location.pathname + '?_pb_reload=' + Date.now() + window.location.hash);
            }}
            style={{
              padding: '12px 24px',
              background: '#D4AF37',
              color: '#0A1428',
              border: 'none',
              borderRadius: 8,
              fontFamily: 'DM Mono, monospace',
              fontSize: 12,
              letterSpacing: 2,
              fontWeight: 700,
              textTransform: 'uppercase',
              cursor: 'pointer',
            }}
          >
            {t('app.lazy_error.reload_clear_cache')}
          </button>
          <div style={{ fontSize: 11, color: '#aaa', maxWidth: 420 }}>
            {t('app.lazy_error.persist_hint_prefix')} <strong>Cmd/Ctrl + Shift + R</strong> {t('app.lazy_error.persist_hint_suffix')}
          </div>
        </div>
      );
    }

    // Erori non-chunk: afișează generic error fallback (nu mai propagăm — nu există
    // boundary mai sus în App.js, ar produce blank page).
    if (this.state.hasError) {
      return (
        <div
          role="alert"
          style={{
            minHeight: '70vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 16,
            padding: 24,
            fontFamily: 'DM Mono, monospace',
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: 16, color: '#c0392b', letterSpacing: 2, textTransform: 'uppercase' }}>
            ⚠️ {t('app.lazy_error.unexpected_title')}
          </div>
          <div style={{ fontSize: 13, color: '#888', maxWidth: 420, lineHeight: 1.5, fontFamily: 'Inter, system-ui, sans-serif' }}>
            {t('app.lazy_error.unexpected_message')}
          </div>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '12px 24px',
              background: '#D4AF37',
              color: '#0A1428',
              border: 'none',
              borderRadius: 8,
              fontFamily: 'DM Mono, monospace',
              fontSize: 12,
              letterSpacing: 2,
              fontWeight: 700,
              textTransform: 'uppercase',
              cursor: 'pointer',
            }}
          >
            {t('app.lazy_error.reload_page')}
          </button>
          {process.env.NODE_ENV !== 'production' && this.state.error && (
            <details style={{ marginTop: 12, maxWidth: 600, textAlign: 'left' }}>
              <summary style={{ cursor: 'pointer', fontSize: 11, color: '#aaa' }}>🔧 {t('app.lazy_error.dev_details')}</summary>
              <pre style={{ fontSize: 10, color: '#c0392b', overflow: 'auto', padding: 12, background: 'rgba(192,57,43,0.05)', borderRadius: 6, marginTop: 8 }}>
                {String(this.state.error?.stack || this.state.error?.message || this.state.error)}
              </pre>
            </details>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

export default withTranslation()(LazyLoadErrorBoundary);
